import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CheckIcon from "@material-ui/icons/Check";
import { setThinkingMode } from "../action";
import "./AssistModeDropdown.css";
import { ASSIST_OPTIONS } from "../constants";

const AssistModeDropdown = ({ label = "GTaxPro Assistant" }) => {
  const dispatch = useDispatch();
  const thinkingMode = useSelector((state) => state.chatBotReducer.thinkingMode);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Keyboard accessibility
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event, value) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(value);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextSibling = event.target.nextElementSibling;
      if (nextSibling) nextSibling.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevSibling = event.target.previousElementSibling;
      if (prevSibling) prevSibling.focus();
    } else if (event.key === "Escape") {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value) => {
    dispatch(setThinkingMode(value));
    setIsOpen(false);
  };

  return (
    <div className="assist-mode-dropdown">
      <div
        ref={triggerRef}
        className="assist-mode-trigger"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        type="div"
      >
        <span className="assist-mode-label">{label}</span>
        <KeyboardArrowDownIcon
          className={`assist-mode-arrow ${isOpen ? "assist-mode-arrow-open" : ""}`}
        />
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="assist-mode-menu"
          role="listbox"
          aria-label="Select assist mode"
        >
          {ASSIST_OPTIONS.map((option, index) => {
            const isSelected = thinkingMode === option.value;
            return (
              <div
                key={option.value}
                className={`assist-mode-option ${isSelected ? "assist-mode-option-selected" : ""}`}
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
              >
                <div className="assist-mode-option-content">
                  <span className="assist-mode-option-title">{option.title}</span>
                  <span className="assist-mode-option-subtitle">{option.subtitle}</span>
                </div>
                {isSelected && (
                  <CheckIcon className="assist-mode-check-icon" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssistModeDropdown;
